import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that manages a build set drawer.
 *
 * @extends {DialogComponent}
 */
class BuildSetDialogComponent extends DialogComponent {
  selectedProducts = {
    product1: null,
    product2: null
  };

  connectedCallback() {
    super.connectedCallback();
    this.resetSelections();
  }

  open() {
    this.showDialog();
    this.resetSelections();
    this.showEmptyState();
  }

  showEmptyState() {
    const emptyState = document.getElementById('buildSetEmptyState');
    const productSelection = document.getElementById('buildSetProductSelection');
    
    if (emptyState) emptyState.style.display = 'flex';
    if (productSelection) productSelection.style.display = 'none';
  }

  showProductSelection() {
    const emptyState = document.getElementById('buildSetEmptyState');
    const productSelection = document.getElementById('buildSetProductSelection');
    
    if (emptyState) emptyState.style.display = 'none';
    if (productSelection) productSelection.style.display = 'block';
    
    this.loadProducts();
  }

  loadProducts() {
    // Load products from a default collection or use a specific collection
    // For now, we'll use a simple approach - you can customize this
    const productOptions = document.querySelectorAll('.product-options');
    if (productOptions.length > 0) {
      // Add some default products or load from a collection
      // This is a placeholder - you'll need to implement the actual product loading
      console.log('Loading products for build set modal');
    }
  }

  close() {
    this.closeDialog();
  }

  resetSelections() {
    this.selectedProducts = {
      product1: null,
      product2: null
    };
    this.updateSelectedProductsDisplay();
  }

  selectProduct(productNumber, productId, productTitle, productPrice, productImage) {
    const productKey = 'product' + productNumber;
    if (productKey === 'product1') {
      this.selectedProducts.product1 = {
        id: productId,
        title: productTitle,
        price: productPrice,
        image: productImage
      };
    } else if (productKey === 'product2') {
      this.selectedProducts.product2 = {
        id: productId,
        title: productTitle,
        price: productPrice,
        image: productImage
      };
    }

    // Update visual selection
    const productOptions = document.querySelectorAll('.build-set-product-selection:nth-child(' + (productNumber + 1) + ') .product-option');
    productOptions.forEach((option) => {
      option.classList.remove('selected');
      const onclickAttr = option.getAttribute('onclick');
      if (onclickAttr && onclickAttr.includes(productId)) {
        option.classList.add('selected');
      }
    });

    this.updateSelectedProductsDisplay();
  }

  updateSelectedProductsDisplay() {
    const summary = document.getElementById('selectedProductsSummary');
    const productsList = document.getElementById('selectedProductsList');
    const totalPrice = document.getElementById('totalPrice');
    const addToCartBtn = document.getElementById('addSetToCart');

    if (!summary || !productsList || !totalPrice || !addToCartBtn) return;

    const hasProduct1 = this.selectedProducts.product1 !== null;
    const hasProduct2 = this.selectedProducts.product2 !== null;

    if (hasProduct1 || hasProduct2) {
      summary.style.display = 'block';
      
      let html = '';
      let total = 0;

      if (hasProduct1 && this.selectedProducts.product1) {
        html += '<div class="build-set-selected-product">' +
          '<img src="' + this.selectedProducts.product1.image + '" alt="' + this.selectedProducts.product1.title + '" class="build-set-selected-product__image">' +
          '<div class="build-set-selected-product__details">' +
            '<h4 class="build-set-selected-product__title">' + this.selectedProducts.product1.title + '</h4>' +
            '<p class="build-set-selected-product__price">$' + this.selectedProducts.product1.price + '</p>' +
          '</div>' +
        '</div>';
        total += parseFloat(this.selectedProducts.product1.price);
      }

      if (hasProduct2 && this.selectedProducts.product2) {
        html += '<div class="build-set-selected-product">' +
          '<img src="' + this.selectedProducts.product2.image + '" alt="' + this.selectedProducts.product2.title + '" class="build-set-selected-product__image">' +
          '<div class="build-set-selected-product__details">' +
            '<h4 class="build-set-selected-product__title">' + this.selectedProducts.product2.title + '</h4>' +
            '<p class="build-set-selected-product__price">$' + this.selectedProducts.product2.price + '</p>' +
          '</div>' +
        '</div>';
        total += parseFloat(this.selectedProducts.product2.price);
      }

      productsList.innerHTML = html;
      totalPrice.textContent = 'Total: $' + total.toFixed(2);
      
      // Show add to cart button if both products are selected
      if (hasProduct1 && hasProduct2) {
        addToCartBtn.style.display = 'block';
        addToCartBtn.textContent = 'Add Set to Cart — $' + total.toFixed(2);
      } else {
        addToCartBtn.style.display = 'none';
      }
    } else {
      summary.style.display = 'none';
      addToCartBtn.style.display = 'none';
    }
  }

  addSetToCart() {
    if (!this.selectedProducts.product1 || !this.selectedProducts.product2) {
      this.showNotification('Please select both products', 'error');
      return;
    }

    const addToCartBtn = document.getElementById('addSetToCart');
    if (!addToCartBtn) return;
    
    const originalText = addToCartBtn.textContent;
    
    // Show loading state
    addToCartBtn.textContent = 'Adding to Cart...';
    addToCartBtn.disabled = true;

    // Add products to cart
    const cartItems = [
      {
        id: this.selectedProducts.product1.id,
        quantity: 1
      },
      {
        id: this.selectedProducts.product2.id,
        quantity: 1
      }
    ];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: cartItems
      })
    })
    .then((response) => {
      if (response.ok) {
        this.showNotification('Set added to cart successfully!', 'success');
        this.close();
        
        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } else {
        throw new Error('Failed to add to cart');
      }
    })
    .catch((error) => {
      console.error('Error adding to cart:', error);
      this.showNotification('Error adding to cart. Please try again.', 'error');
    })
    .finally(() => {
      // Reset button state
      addToCartBtn.textContent = originalText;
      addToCartBtn.disabled = false;
    });
  }

  showNotification(message, type) {
    // Create notification element
    const notification = document.createElement('div');
    notification.textContent = message;
    const backgroundColor = type === 'success' ? '#4CAF50' : type === 'error' ? '#f44336' : '#333';
    
    notification.style.cssText = 'position: fixed; top: 20px; right: 20px; background: ' + backgroundColor + '; color: white; padding: 12px 20px; border-radius: 4px; z-index: 1001; font-size: 14px; box-shadow: 0 2px 10px rgba(0,0,0,0.2); transform: translateX(100%); transition: transform 0.3s ease; max-width: 300px;';
    
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 10);
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }
}

if (!customElements.get('build-set-dialog-component')) {
  customElements.define('build-set-dialog-component', BuildSetDialogComponent);
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  console.log('Build Set Cart initialized');
});

// Global functions for onclick handlers
window.openBuildSetModal = function() {
  console.log('openBuildSetModal called');
  const dialog = document.getElementById('build-set-dialog');
  if (dialog && dialog.open) {
    dialog.open();
  } else {
    console.error('Build set dialog not found or open method not available');
  }
}

window.closeBuildSetModal = function() {
  const dialog = document.getElementById('build-set-dialog');
  if (dialog && dialog.close) {
    dialog.close();
  }
}

window.selectProduct = function(productNumber, productId, productTitle, productPrice, productImage) {
  const dialog = document.getElementById('build-set-dialog');
  if (dialog && dialog.selectProduct) {
    dialog.selectProduct(productNumber, productId, productTitle, productPrice, productImage);
  }
}

window.selectBuildSetProduct = function(productNumber, productId, productTitle, productPrice, productImage) {
  const dialog = document.getElementById('build-set-dialog');
  if (dialog && dialog.selectProduct) {
    dialog.selectProduct(productNumber, productId, productTitle, productPrice, productImage);
  }
}

window.addSetToCart = function() {
  const dialog = document.getElementById('build-set-dialog');
  if (dialog && dialog.addSetToCart) {
    dialog.addSetToCart();
  }
}

window.addBuildSetToCart = function() {
  const productItems = document.querySelectorAll('.build-set-product-item');
  const cartItems = [];
  
  console.log('Found product items:', productItems.length);
  
  productItems.forEach((item, index) => {
    const productId = item.dataset.productId;
    const variantSelect = item.querySelector('.build-set-variant-select');
    const quantityInput = item.querySelector('.quantity-input');
    
    console.log(`Product ${index + 1}:`, {
      productId: productId,
      variantSelect: variantSelect,
      quantityInput: quantityInput
    });
    
    let variantId = productId; // Default to product ID if no variants
    if (variantSelect) {
      variantId = variantSelect.value;
    }
    
    const quantity = parseInt(quantityInput.value) || 1;
    
    console.log(`Adding to cart:`, {
      id: variantId,
      quantity: quantity
    });
    
    cartItems.push({
      id: variantId,
      quantity: quantity
    });
  });
  
  console.log('Cart items:', cartItems);
  
  if (cartItems.length === 0) {
    showNotification('No products selected', 'error');
    return;
  }
  
  const addToCartBtn = document.getElementById('addSetToCart');
  if (!addToCartBtn) return;
  
  const originalText = addToCartBtn.textContent;
  
  // Show loading state
  addToCartBtn.textContent = 'Adding to Cart...';
  addToCartBtn.disabled = true;
  
  // Add products to cart
  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      items: cartItems
    })
  })
  .then((response) => {
    if (response.ok) {
      showNotification('Set added to cart successfully!', 'success');
      closeBuildSetModal();
      
      // Trigger multiple cart update events
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:add'));
      
      // Force page refresh for cart update
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      throw new Error('Failed to add to cart');
    }
  })
  .catch((error) => {
    console.error('Error adding to cart:', error);
    showNotification('Error adding to cart. Please try again.', 'error');
  })
  .finally(() => {
    // Reset button state
    addToCartBtn.textContent = originalText;
    addToCartBtn.disabled = false;
  });
}

window.changeQuantity = function(productId, change) {
  const quantityInput = document.getElementById(`quantity-${productId}`);
  if (!quantityInput) return;
  
  const currentQuantity = parseInt(quantityInput.value) || 1;
  const newQuantity = Math.max(1, currentQuantity + change);
  
  quantityInput.value = newQuantity;
  updateTotalPrice();
}

window.updateTotalPrice = function() {
  const productItems = document.querySelectorAll('.build-set-product-item');
  let total = 0;
  
  productItems.forEach(item => {
    const variantSelect = item.querySelector('.build-set-variant-select');
    const quantityInput = item.querySelector('.quantity-input');
    const priceElement = item.querySelector('.build-set-product-price');
    
    if (variantSelect && quantityInput && priceElement) {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      const price = parseFloat(selectedOption.dataset.price) || 0;
      const quantity = parseInt(quantityInput.value) || 1;
      
      total += price * quantity;
    }
  });
  
  const totalPriceElement = document.getElementById('buildSetTotalPrice');
  if (totalPriceElement) {
    // Format price (assuming currency is TL)
    totalPriceElement.textContent = `${total.toFixed(2)} TL`;
  }
}

window.startBuildingSet = function() {
  const dialog = document.getElementById('build-set-dialog');
  if (dialog && dialog.showProductSelection) {
    dialog.showProductSelection();
  }
}

// Notification function
window.showNotification = function(message, type) {
  // Create notification element
  const notification = document.createElement('div');
  notification.textContent = message;
  const backgroundColor = type === 'success' ? '#4CAF50' : type === 'error' ? '#f44336' : '#333';
  
  notification.style.cssText = 'position: fixed; top: 20px; right: 20px; background: ' + backgroundColor + '; color: white; padding: 12px 20px; border-radius: 4px; z-index: 1001; font-size: 14px; box-shadow: 0 2px 10px rgba(0,0,0,0.2); transform: translateX(100%); transition: transform 0.3s ease; max-width: 300px;';
  
  document.body.appendChild(notification);
  
  // Animate in
  setTimeout(() => {
    notification.style.transform = 'translateX(0)';
  }, 10);
  
  // Remove after 3 seconds
  setTimeout(() => {
    notification.style.transform = 'translateX(100%)';
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 300);
  }, 3000);
}

// Initialize total price calculation when drawer opens
document.addEventListener('DOMContentLoaded', function() {
  // Add event listeners for variant changes
  document.addEventListener('change', function(e) {
    if (e.target.classList.contains('build-set-variant-select')) {
      updateTotalPrice();
    }
  });
  
  // Add event listeners for quantity changes
  document.addEventListener('input', function(e) {
    if (e.target.classList.contains('quantity-input')) {
      updateTotalPrice();
    }
  });
});